import React, { ReactElement, useEffect, useState } from "react";

import axios from "axios";

import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";

import { reverseMersDocument } from "Adapters/Mers/mersDocumentAdapter";

import { getMersApiResponseError } from "Common/Utilities";

import { IMersDocumentApiResponse } from "Features/Vault/VaultInterfaces";

export interface ReverseDocumentModalProps {
  documentId: number;
  documentName: string;
  mersDocumentType: string | null;
  onClose: () => void;
  onSuccess: () => void;
  open: boolean;
  packageId: string;
}

function toSpacedLabel(type: string): string {
  return type.replace(/([A-Z])/g, " $1").trim();
}

export function ReverseDocumentModal({
  documentId,
  documentName,
  mersDocumentType,
  onClose,
  onSuccess,
  open,
  packageId,
}: ReverseDocumentModalProps): ReactElement {
  const [submitError, setSubmitError] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitError("");
      setSubmitting(false);
    }
  }, [open]);

  async function handleSubmit(): Promise<void> {
    if (submitting) {
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    try {
      const data = await reverseMersDocument(packageId, documentId);
      const errorMessage = getMersApiResponseError(data);
      if (errorMessage) {
        setSubmitError(errorMessage);
        return;
      }
      onSuccess();
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const errorMessage = getMersApiResponseError(err.response?.data as IMersDocumentApiResponse);
        setSubmitError(errorMessage ?? "An error occurred. Please try again.");
      } else {
        setSubmitError("An error occurred. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const typeLabel = mersDocumentType ? toSpacedLabel(mersDocumentType) : "supplemental document";

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <DialogTitle>Reverse document addition</DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ mb: 2 }} variant="body2">
          This reverses the prior Add Document transaction on MERS. The file stays in vault history.
        </Typography>
        <Typography variant="body2">
          <strong>Document:</strong> {documentName}
        </Typography>
        <Typography sx={{ mt: 1 }} variant="body2">
          <strong>MERS type:</strong> {typeLabel}
        </Typography>

        {submitting && (
          <Typography align="center" sx={{ display: "block", mt: 2 }}>
            <CircularProgress size={24} />
          </Typography>
        )}

        {submitError && (
          <Typography color="error" sx={{ display: "block", mt: 2 }} variant="caption">
            {submitError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={submitting} onClick={onClose}>
          Cancel
        </Button>
        <Button color="error" disabled={submitting} onClick={() => void handleSubmit()} variant="contained">
          {submitting ? "Submitting..." : "Reverse on MERS"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
